import Raven from 'raven-js';
import {
  RECURRING_BILLING_FREQUENCIES,
  RecurringBillingFrequency,
} from '../constants/RecurringBillingFrequencies';
import { IntervalUnit } from '../types/IntervalUnit';
import Maybe from '../types/utils/Maybe';

export type BillingInterval = RecurringBillingFrequency | 'ONE_TIME';

export function getRecurringBillingFrequency(
  recurringBillingTerms: {
    intervalLength: number;
    intervalUnit: IntervalUnit;
  } | null
): Maybe<RecurringBillingFrequency> {
  if (!recurringBillingTerms) {
    return null;
  }

  const { intervalLength, intervalUnit } = recurringBillingTerms;

  if (intervalLength === 1 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.MINUTES;
  } else if (intervalLength === 2 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.TWO_MINUTES;
  } else if (intervalLength === 3 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.THREE_MINUTES;
  } else if (intervalLength === 4 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.FOUR_MINUTES;
  } else if (intervalLength === 5 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.FIVE_MINUTES;
  } else if (intervalLength === 10 && intervalUnit === 'MINUTES') {
    return RECURRING_BILLING_FREQUENCIES.TEN_MINUTES;
  } else if (intervalLength === 1 && intervalUnit === 'DAYS') {
    return RECURRING_BILLING_FREQUENCIES.DAYS;
  } else if (intervalLength === 1 && intervalUnit === 'WEEKS') {
    return RECURRING_BILLING_FREQUENCIES.WEEKS;
  } else if (intervalLength === 2 && intervalUnit === 'WEEKS') {
    return RECURRING_BILLING_FREQUENCIES.TWO_WEEKS;
  } else if (intervalLength === 1 && intervalUnit === 'MONTHS') {
    return RECURRING_BILLING_FREQUENCIES.MONTHS;
  } else if (intervalLength === 3 && intervalUnit === 'MONTHS') {
    return RECURRING_BILLING_FREQUENCIES.QUARTERS;
  } else if (intervalLength === 6 && intervalUnit === 'MONTHS') {
    return RECURRING_BILLING_FREQUENCIES.SIX_MONTHS;
  } else if (intervalLength === 1 && intervalUnit === 'YEARS') {
    return RECURRING_BILLING_FREQUENCIES.YEARS;
  } else if (intervalLength === 2 && intervalUnit === 'YEARS') {
    return RECURRING_BILLING_FREQUENCIES.TWO_YEARS;
  } else if (intervalLength === 3 && intervalUnit === 'YEARS') {
    return RECURRING_BILLING_FREQUENCIES.THREE_YEARS;
  } else {
    const error = new Error('Unsupported recurring billing configuration');
    Raven.captureException(error);
    throw error;
  }
}

/**
 * @deprecated Use getRecurringBillingFrequency instead. Kept as a thin wrapper so
 * dunning-ui keeps building until it migrates off this name; remove once it does.
 */
export function getLineItemRecurringFrequency(
  recurringBillingTerms: {
    intervalLength: number;
    intervalUnit: IntervalUnit;
  } | null
) {
  return getRecurringBillingFrequency(recurringBillingTerms);
}

/**
 * @deprecated Use getRecurringBillingFrequency({ intervalUnit, intervalLength })
 * instead. Kept as a thin wrapper so dunning-ui keeps building until it migrates
 * off this name; remove once it does.
 */
export function getLineItemRecurringFrequencyBySubtotal(
  intervalUnit: IntervalUnit,
  intervalLength: number
): BillingInterval {
  // Non-null: a non-null terms object is always passed, so the null branch of
  // getRecurringBillingFrequency is unreachable here. The explicit return type
  // preserves the original non-nullable contract for dunning-ui.
  return getRecurringBillingFrequency({ intervalUnit, intervalLength })!;
}
